import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "../imports";
import { useClientPrincipal } from "./ClientPrincipalContext";

type DisclaimerContextType = {
  showDisclaimer: boolean;
  acceptDisclaimer: () => void;
  hasSeenDisclaimer: boolean;
};

const DisclaimerContext = createContext<DisclaimerContextType | undefined>(
  undefined,
);

export const useDisclaimer = () => {
  const context = useContext(DisclaimerContext);
  if (context === undefined) {
    throw new Error("useDisclaimer must be used within a DisclaimerProvider");
  }
  return context;
};

const DISCLAIMER_STORAGE_KEY = "shortfy_disclaimer_accepted";

export const DisclaimerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { clientPrincipal, isLoading } = useClientPrincipal();
  const isLoggedIn = !!clientPrincipal?.userDetails;

  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useState(() => {
    return localStorage.getItem(DISCLAIMER_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    if (isLoading) return;

    if (!isLoggedIn) {
      localStorage.removeItem(DISCLAIMER_STORAGE_KEY);
      setHasSeenDisclaimer(false);
      setShowDisclaimer(false);
    } else {
      const disclaimerAccepted = localStorage.getItem(DISCLAIMER_STORAGE_KEY);

      if (disclaimerAccepted === "true") {
        setHasSeenDisclaimer(true);
        setShowDisclaimer(false);
      } else {
        setHasSeenDisclaimer(false);
        setShowDisclaimer(true);
      }
    }
  }, [isLoggedIn, isLoading]);

  const acceptDisclaimer = useCallback(() => {
    localStorage.setItem(DISCLAIMER_STORAGE_KEY, "true");
    setHasSeenDisclaimer(true);
    setShowDisclaimer(false);
  }, []);

  const contextValue: DisclaimerContextType = {
    showDisclaimer,
    acceptDisclaimer,
    hasSeenDisclaimer,
  };

  return (
    <DisclaimerContext.Provider value={contextValue}>
      {children}
    </DisclaimerContext.Provider>
  );
};
